import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { productUrl } from '../utils/urls';
import { optimizeImageUrl } from '../utils/imageUrl';
import { useTracking } from '../hooks/useTracking';
import { useAuth } from '../contexts/AuthContext';
import { rankProducts, fetchRankingProfile } from '../utils/personalizedRanking';
import { calculateFinalPrice, calculateFullPrice, getTotalDiscountPercentage } from '../lib/feeCalculator';
import { sortProductsByAvailability, getProductAvailability } from '../utils/productAvailability';
import { getChildCategoryName } from '../utils/categoryPath';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { 
  Search, 
  Filter, 
  ShoppingBag, 
  X, 
  ChevronDown, 
  ChevronRight,
  SlidersHorizontal,
  ArrowUpDown,
  Loader2,
  Package,
  Grid3X3,
  LayoutGrid,
  Star,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PRODUCTS_PER_PAGE = 24;

// Price ranges
const PRICE_RANGES = [
  { id: 'all', label: 'Tutti i prezzi', min: null, max: null },
  { id: 'under50', label: 'Sotto €50', min: 0, max: 49.99 },
  { id: '50to100', label: '€50 - €100', min: 50, max: 100 },
  { id: '100to200', label: '€100 - €200', min: 100, max: 200 },
  { id: 'over200', label: 'Oltre €200', min: 200.01, max: null }
];

// Sort options
const SORT_OPTIONS = [
  { id: 'recommended', label: 'Consigliati per te', field: 'popularity_score', ascending: false },
  { id: 'newest', label: 'Più recenti', field: 'created_at', ascending: false },
  { id: 'price_low', label: 'Prezzo: crescente', field: 'base_price', ascending: true },
  { id: 'price_high', label: 'Prezzo: decrescente', field: 'base_price', ascending: false },
  { id: 'name_az', label: 'Nome: A-Z', field: 'title', ascending: true }
];

// Artisanal title variations
const ARTISAN_TITLES = [
  'Creazioni Artigianali',
  'Fatto a Mano',
  'Artigianato Italiano',
  'Handmade',
  'Opere d\'Arte',
  'Creazioni Uniche'
];

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackSearch, trackCategoryView } = useTracking();
  const { user } = useAuth();
  const rankingProfileRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Random artisan title - fixed per session to avoid flickering
  const [artisanTitle] = useState(() => 
    ARTISAN_TITLES[Math.floor(Math.random() * ARTISAN_TITLES.length)]
  );
  
  // State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [productReviews, setProductReviews] = useState({});
  const [isLaunchMode, setIsLaunchMode] = useState(null); // null = not loaded yet
  const [launchModeLoaded, setLaunchModeLoaded] = useState(false);
  
  // Categories from DB
  const [dbCategories, setDbCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Filters state
  const [localSearchInput, setLocalSearchInput] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Read from URL params - default to 'all' for category
  const searchQuery = searchParams.get('search') || '';
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedSubcategory = searchParams.get('subcategory') || '';
  const selectedSubSubcategory = searchParams.get('subsubcategory') || '';
  const priceRange = searchParams.get('price') || 'all';
  const sortBy = searchParams.get('sort') || 'recommended';

  // Load launch mode status
  useEffect(() => {
    const loadLaunchMode = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/launch-mode`);
        if (response.ok) {
          const data = await response.json();
          const isActive = data.launch_mode === true;
          setIsLaunchMode(isActive);
          // Sync to localStorage
          localStorage.setItem('rarifex_launch_mode', isActive.toString());
        }
      } catch (error) {
        // Fallback to localStorage
        setIsLaunchMode(localStorage.getItem('rarifex_launch_mode') === 'true');
      } finally {
        setLaunchModeLoaded(true);
      }
    };
    loadLaunchMode();
  }, []);

  // Sync local input with URL
  useEffect(() => {
    setLocalSearchInput(searchQuery);
  }, [searchQuery]);

  // Load categories from DB (only active ones)
  useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .or('is_active.eq.true,is_active.is.null')
        .order('name', { ascending: true });
      
      if (!error && data) {
        setDbCategories(data);
      } else {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Organize categories into hierarchy (recursive tree structure)
  const categoryTree = useMemo(() => {
    // Build a recursive tree structure
    const buildTree = (parentId = null) => {
      return dbCategories
        .filter(c => c.parent_id === parentId)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(cat => ({
          ...cat,
          children: buildTree(cat.id)
        }));
    };
    return buildTree(null);
  }, [dbCategories]);

  // Update URL params
  const updateParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
    setPage(0);
    setProducts([]);
  }, [searchParams, setSearchParams]);

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    if (localSearchInput.trim()) {
      trackSearch(localSearchInput.trim());
    }
    updateParams({ search: localSearchInput.trim() });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
    setLocalSearchInput('');
    setPage(0);
    setProducts([]);
  };

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedCategory !== 'all') count++;
    if (selectedSubcategory) count++;
    if (selectedSubSubcategory) count++;
    if (priceRange !== 'all') count++;
    return count;
  }, [searchQuery, selectedCategory, selectedSubcategory, selectedSubSubcategory, priceRange]);

  // Load products
  const loadProducts = useCallback(async (pageNum = 0, append = false) => {
    if (pageNum === 0) setLoading(true);
    else setLoadingMore(true);

    const from = pageNum * PRODUCTS_PER_PAGE;
    const to = from + PRODUCTS_PER_PAGE - 1;

    let query = supabase
      .from('products')
      .select(`
        *,
        profiles:crafter_id (
          id,
          full_name,
          avatar_url,
          tier,
          rarifex_verified
        )
      `, { count: 'exact' })
      .eq('status', 'active');

    // Category filter - use customization_options->categoryPath for accurate filtering
    if (selectedCategory !== 'all') {
      if (selectedSubSubcategory) {
        // Filter by third-level category (child) in categoryPath.child
        const subSubCat = dbCategories.find(c => c.slug === selectedSubSubcategory);
        const subSubCatName = subSubCat?.name || selectedSubSubcategory;
        query = query.ilike('customization_options->categoryPath->>child', `%${subSubCatName}%`);
      } else if (selectedSubcategory) {
        // Filter by subcategory name in categoryPath.sub
        const subCat = dbCategories.find(c => c.slug === selectedSubcategory);
        const subCatName = subCat?.name || selectedSubcategory;
        query = query.ilike('customization_options->categoryPath->>sub', `%${subCatName}%`);
      } else {
        // Filter by main category slug
        query = query.ilike('category', `%${selectedCategory}%`);
      }
    }

    // Search filter
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
    }

    // Price range filter
    const priceConfig = PRICE_RANGES.find(p => p.id === priceRange);
    if (priceConfig && priceConfig.min !== null) {
      query = query.gte('base_price', priceConfig.min);
    }
    if (priceConfig && priceConfig.max !== null) {
      query = query.lte('base_price', priceConfig.max);
    }

    // Sort
    const sortConfig = SORT_OPTIONS.find(s => s.id === sortBy) || SORT_OPTIONS[0];
    if (sortConfig.id === 'recommended') {
      query = query.order('popularity_score', { ascending: false, nullsFirst: false }).order('created_at', { ascending: false });
    } else {
      query = query.order(sortConfig.field, { ascending: sortConfig.ascending });
    }

    // Pagination
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (!error && data) {
      // Load review stats for products
      const productIds = data.map(p => p.id);
      if (productIds.length > 0) {
        const { data: reviewsData } = await supabase
          .from('reviews')
          .select('product_id, rating')
          .in('product_id', productIds);
        
        if (reviewsData && reviewsData.length > 0) {
          const reviewMap = {};
          reviewsData.forEach(r => {
            if (!reviewMap[r.product_id]) {
              reviewMap[r.product_id] = { total: 0, count: 0 };
            }
            reviewMap[r.product_id].total += r.rating;
            reviewMap[r.product_id].count++;
          });
          
          // Calculate averages
          Object.keys(reviewMap).forEach(productId => {
            reviewMap[productId].average = reviewMap[productId].total / reviewMap[productId].count;
          });
          
          setProductReviews(prev => ({ ...prev, ...reviewMap }));
        }
      }
      
      // Apply availability sorting - out-of-stock products at the end
      // Per "Consigliati per te": ranking personalizzato (affinità) oltre all'ordine per popolarità dal DB.
      let sortedData = sortProductsByAvailability(data);
      const optedOut = (() => { try { return localStorage.getItem('rarifex_tracking_optout') === 'true'; } catch { return false; } })();

      if (sortBy === 'recommended' && !optedOut) {
        if (!rankingProfileRef.current) {
          const sessionId = localStorage.getItem('rarifex_session_id');
          rankingProfileRef.current = await fetchRankingProfile(API_URL, user?.id, sessionId) || { personalized: false };
        }
        const profile = rankingProfileRef.current;
        if (profile && profile.personalized) {
          sortedData = rankProducts(data, profile);
        }
      }
      
      if (append) {
        setProducts(prev => {
          // When appending, we need to re-sort the entire array
          const combined = [...prev, ...sortedData];
          if (sortBy === 'recommended' && !optedOut && rankingProfileRef.current?.personalized) {
            return rankProducts(combined, rankingProfileRef.current);
          }
          return sortProductsByAvailability(combined);
        });
      } else {
        setProducts(sortedData);
      }
      setTotalCount(count || 0);
      setHasMore(data.length === PRODUCTS_PER_PAGE);
      setPage(pageNum);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [selectedCategory, selectedSubcategory, selectedSubSubcategory, searchQuery, priceRange, sortBy, dbCategories, user?.id, API_URL]);

  // Reset del profilo affinità quando cambia l'utente (login/logout)
  useEffect(() => {
    rankingProfileRef.current = null;
  }, [user?.id]);

  // Load on filter change
  useEffect(() => {
    loadProducts(0, false);
    
    // Track category view
    if (selectedCategory !== 'all') {
      trackCategoryView(selectedSubSubcategory || selectedSubcategory || selectedCategory);
    }
  }, [selectedCategory, selectedSubcategory, selectedSubSubcategory, searchQuery, priceRange, sortBy]);

  // Load more handler
  const handleLoadMore = () => {
    loadProducts(page + 1, true);
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Get current category name for SEO
  const currentCategoryName = useMemo(() => {
    if (selectedSubSubcategory) {
      const subSub = dbCategories.find(c => c.slug === selectedSubSubcategory);
      return subSub?.name || '';
    }
    if (selectedSubcategory) {
      const sub = dbCategories.find(c => c.slug === selectedSubcategory);
      return sub?.name || '';
    }
    if (selectedCategory !== 'all') {
      const main = dbCategories.find(c => c.slug === selectedCategory);
      return main?.name || '';
    }
    return '';
  }, [selectedCategory, selectedSubcategory, selectedSubSubcategory, dbCategories]);

  // SEO title and description
  const seoTitle = searchQuery 
    ? `Risultati per "${searchQuery}" | Rarifex`
    : currentCategoryName 
      ? `${currentCategoryName} - ${artisanTitle} | Rarifex`
      : 'Catalogo Prodotti Artigianali Made in Italy | Rarifex';
  
  const seoDescription = searchQuery
    ? `Trova prodotti artigianali per "${searchQuery}". Creazioni uniche fatte a mano da artigiani italiani.`
    : currentCategoryName
      ? `Scopri la nostra selezione di ${currentCategoryName.toLowerCase()} artigianali. Prodotti unici fatti a mano da maestri artigiani italiani.`
      : 'Esplora il catalogo completo di prodotti artigianali Made in Italy. Creazioni uniche, fatte a mano da maestri artigiani italiani.';

  // Render function for recursive category item (not a component to avoid re-render issues)
  const renderCategoryItem = (category, level = 0, parentSlug = null, grandparentSlug = null, onClose = null) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories[category.id];
    
    // Determine if this category is selected based on level
    const isSelected = level === 0 
      ? selectedCategory === category.slug && !selectedSubcategory
      : level === 1
        ? selectedSubcategory === category.slug && !selectedSubSubcategory
        : selectedSubSubcategory === category.slug;
    
    // Build URL params based on level
    const getCategoryParams = () => {
      if (level === 0) {
        return { category: category.slug, subcategory: '', subsubcategory: '' };
      } else if (level === 1) {
        return { category: parentSlug, subcategory: category.slug, subsubcategory: '' };
      } else {
        return { category: grandparentSlug, subcategory: parentSlug, subsubcategory: category.slug };
      }
    };
    
    // Colors vary slightly by depth for visual hierarchy
    const depthColors = [
      { bg: 'bg-[#008080]', text: 'text-white', hover: 'hover:bg-slate-100', textDefault: 'text-slate-700' },
      { bg: 'bg-[#008080]/10', text: 'text-[#008080]', hover: 'hover:bg-slate-50', textDefault: 'text-slate-600' },
      { bg: 'bg-[#DAA520]/10', text: 'text-[#DAA520]', hover: 'hover:bg-slate-50', textDefault: 'text-slate-500' },
    ];
    const colors = depthColors[Math.min(level, depthColors.length - 1)];
    
    // Indentation wrapper class based on level
    const wrapperClass = level === 0 
      ? '' 
      : level === 1 
        ? 'ml-4 mt-1 space-y-0.5 border-l-2 border-slate-100 pl-3' 
        : 'ml-3 mt-0.5 space-y-0.5 border-l border-slate-100/50 pl-2';
    
    return (
      <div key={category.id} className={level > 0 ? wrapperClass : ''}>
        <button
          onClick={() => {
            if (hasChildren) {
              toggleCategory(category.id);
            } else {
              updateParams(getCategoryParams());
              onClose?.();
            }
          }}
          className={`w-full text-left px-3 py-${level === 0 ? '2' : '1.5'} rounded-lg text-sm transition-colors flex items-center justify-between ${
            isSelected
              ? `${colors.bg} ${colors.text} font-medium` 
              : `${colors.textDefault} ${colors.hover}`
          }`}
        >
          <span className={`flex items-center gap-2 ${level > 1 ? 'text-xs' : ''}`}>
            {category.name}
          </span>
          {hasChildren && (
            <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${
              isExpanded ? 'rotate-180' : ''
            }`} />
          )}
        </button>
        
        {/* Children (recursive) */}
        {hasChildren && isExpanded && (
          <div className={level === 0 ? 'ml-4 mt-1 space-y-0.5 border-l-2 border-slate-100 pl-3' : 'mt-0.5 space-y-0.5'}>
            {/* "View all" button for parent with children */}
            <button
              onClick={() => {
                updateParams(getCategoryParams());
                onClose?.();
              }}
              className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-colors font-medium ${
                isSelected
                  ? `${colors.bg} ${colors.text}` 
                  : `text-[#008080] ${colors.hover}`
              }`}
            >
              Tutti in {category.name}
            </button>
            
            {/* Render children recursively */}
            {category.children.map((child) => 
              renderCategoryItem(
                child,
                level + 1,
                level === 0 ? category.slug : parentSlug,
                level === 0 ? null : (level === 1 ? parentSlug : grandparentSlug),
                onClose
              )
            )}
          </div>
        )}
      </div>
    );
  };

  // Filter sidebar component (reused for mobile drawer)
  const FilterSidebar = ({ onClose }) => (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Grid3X3 className="w-4 h-4" />
          Categorie
        </h3>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1 scrollbar-thin">
          <button
            onClick={() => {
              updateParams({ category: 'all', subcategory: '', subsubcategory: '' });
              onClose?.();
            }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              selectedCategory === 'all' 
                ? 'bg-[#008080] text-white font-medium' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Tutte le categorie
            {selectedCategory === 'all' && (
              <span className="float-right text-white/70">({totalCount})</span>
            )}
          </button>
          
          {categoryTree.map((category) => 
            renderCategoryItem(category, 0, null, null, onClose)
          )}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Fascia di prezzo
        </h3>
        <div className="space-y-1">
          {PRICE_RANGES.map((range) => (
            <button
              key={range.id}
              onClick={() => {
                updateParams({ price: range.id });
                onClose?.();
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                priceRange === range.id 
                  ? 'bg-[#DAA520]/10 text-[#DAA520] font-medium border border-[#DAA520]/30' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Clear filters button */}
      {activeFiltersCount > 0 && (
        <Button
          variant="outline"
          onClick={() => {
            clearAllFilters();
            onClose?.();
          }}
          className="w-full text-slate-600"
        >
          <X className="w-4 h-4 mr-2" />
          Rimuovi filtri ({activeFiltersCount})
        </Button>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="prodotti artigianali, made in italy, artigianato italiano, fatto a mano, handmade, catalogo" />
        <link rel="canonical" href={`https://www.rarifex.com/products${searchParams.toString() ? '?' + searchParams.toString() : ''}`} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-slate-50" data-testid="products-page">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#008080] to-[#006666] text-white py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <nav className="flex items-center gap-2 text-white/70 text-sm mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Catalogo</span>
              {currentCategoryName && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-white">{currentCategoryName}</span>
                </>
              )}
            </nav>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              {searchQuery 
                ? `Risultati per "${searchQuery}"`
                : currentCategoryName 
                  ? `${currentCategoryName} - ${artisanTitle}`
                  : 'Catalogo Prodotti'}
            </h1>
            <p className="text-white/80 max-w-2xl">
              {searchQuery 
                ? `${totalCount} prodotti trovati per la tua ricerca`
                : 'Esplora le creazioni uniche dei nostri artigiani italiani. Ogni pezzo è fatto a mano con passione e maestria.'}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Search Bar + Sort + Mobile Filter Toggle */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Cerca prodotti artigianali..."
                    value={localSearchInput}
                    onChange={(e) => setLocalSearchInput(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="page-search-input"
                  />
                </div>
                <Button type="submit" className="bg-[#008080] hover:bg-[#006666] h-11 px-6">
                  <Search className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Cerca</span>
                </Button>
              </form>

              {/* Sort dropdown */}
              <div className="flex gap-2">
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => updateParams({ sort: e.target.value })}
                    className="h-11 pl-10 pr-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#008080]/20"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                  <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>

                {/* Mobile filter toggle */}
                <Button
                  variant="outline"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="md:hidden h-11 relative"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filtri
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#008080] text-white text-xs rounded-full flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Active filters pills */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                {searchQuery && (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700 px-3 py-1 flex items-center gap-1">
                    <Search className="w-3 h-3" />
                    {searchQuery}
                    <button onClick={() => updateParams({ search: '' })} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedCategory !== 'all' && (
                  <Badge variant="secondary" className="bg-[#008080]/10 text-[#008080] px-3 py-1 flex items-center gap-1">
                    <Grid3X3 className="w-3 h-3" />
                    {currentCategoryName}
                    <button onClick={() => updateParams({ category: 'all', subcategory: '', subsubcategory: '' })} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {priceRange !== 'all' && (
                  <Badge variant="secondary" className="bg-[#DAA520]/10 text-[#DAA520] px-3 py-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {PRICE_RANGES.find(p => p.id === priceRange)?.label}
                    <button onClick={() => updateParams({ price: 'all' })} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                <button 
                  onClick={clearAllFilters}
                  className="text-sm text-slate-500 hover:text-slate-700 underline"
                >
                  Rimuovi tutti
                </button>
              </div>
            )}
          </div>

          {/* Main content with sidebar */}
          <div className="flex gap-8">
            {/* Desktop Sidebar */}
            <aside className="hidden md:block w-64 flex-shrink-0">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sticky top-24">
                <FilterSidebar />
              </div>
            </aside>

            {/* Products Grid */}
            <main className="flex-1 min-w-0">
              {/* Results count */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-600">
                  {loading ? (
                    'Caricamento...'
                  ) : (
                    <>
                      <span className="font-semibold text-slate-900">{totalCount}</span> prodotti trovati
                    </>
                  )}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-100 overflow-hidden animate-pulse">
                      <div className="h-48 bg-slate-100" />
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-slate-100 rounded w-3/4" />
                        <div className="h-3 bg-slate-100 rounded w-1/2" />
                        <div className="h-5 bg-slate-100 rounded w-1/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length > 0 ? (
                <>
                  <div key={`grid-${launchModeLoaded}-${isLaunchMode}`} className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
                    {products.map((product, index) => {
                      const mainImgIndex = product.customization_options?.main_image_index || 0;
                      const mainImage = optimizeImageUrl(product.images?.[mainImgIndex] || product.images?.[0], 500);
                      const crafterDiscount = product.customization_options?.crafterDiscount || 0;
                      const launchActive = launchModeLoaded ? (isLaunchMode === true) : false;
                      const hasDiscount = crafterDiscount > 0 || launchActive;
                      const originalPrice = calculateFullPrice(product.base_price);
                      const finalPrice = calculateFinalPrice(product.base_price, crafterDiscount, launchActive);
                      const totalDiscountPercent = getTotalDiscountPercentage(crafterDiscount, launchActive);
                      
                      return (
                        <Link
                          key={product.id}
                          to={productUrl(product)}
                          className="group block"
                          data-testid={`product-card-${index}`}
                        >
                          <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full border-slate-100 bg-white flex flex-col">
                            <div className="relative aspect-square bg-slate-100 flex-shrink-0">
                              {mainImage ? (
                                <img 
                                  src={mainImage} 
                                  alt={product.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Package className="w-12 h-12 text-slate-300" />
                                </div>
                              )}
                              
                              {/* Badges */}
                              <div className="absolute top-2 left-2 flex flex-col gap-1">
                                {product.category && (
                                  <Badge className="bg-white/95 text-slate-700 text-xs shadow-sm">
                                    {getChildCategoryName(product.customization_options?.categoryPath) ||
                                     product.customization_options?.categoryPath?.sub ||
                                     product.category}
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="absolute top-2 right-2 flex flex-col gap-1">
                                {product.profiles?.tier === 'pro' && (
                                  <Badge className="bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-white text-xs">
                                    <Star className="w-3 h-3 mr-0.5 fill-current" />
                                    Pro
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Availability + customization badges - bottom left */}
                              {(() => {
                                const { isAvailable, isOnDemand } = getProductAvailability(product);
                                const hasCustomizations = product.customization_options?.customizations?.length > 0;
                                if (!isAvailable) {
                                  return (
                                    <Badge className="absolute bottom-2 left-2 bg-red-500 text-white text-xs">
                                      Esaurito
                                    </Badge>
                                  );
                                }
                                if (hasCustomizations) {
                                  return (
                                    <Badge className="absolute bottom-2 left-2 bg-[#DAA520] text-white text-xs">
                                      Personalizzabile
                                    </Badge>
                                  );
                                }
                                if (isOnDemand) {
                                  return (
                                    <Badge className="absolute bottom-2 left-2 bg-[#008080] text-white text-xs">
                                      Su commissione
                                    </Badge>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            
                            <div className="p-4 flex flex-col flex-1">
                              <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1 group-hover:text-[#008080] transition-colors">
                                {product.title}
                              </h3>
                              
                              {/* Crafter info */}
                              {product.profiles && (
                                <div className="flex items-center gap-2 mb-2">
                                  <div
                                    className="w-5 h-5 rounded-full bg-slate-200 bg-cover bg-center flex-shrink-0"
                                    style={{
                                      backgroundImage: product.profiles.avatar_url ? `url(${optimizeImageUrl(product.profiles.avatar_url, 48)})` : undefined,
                                    }}
                                  />
                                  <span className="text-xs text-slate-500 truncate">
                                    {product.profiles.full_name || 'Artigiano'}
                                  </span>
                                </div>
                              )}
                              
                              {/* Review preview - min-height ensures consistent card height */}
                              <div className="min-h-[20px] mb-2">
                                {productReviews[product.id] && productReviews[product.id].count > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Star className="w-3.5 h-3.5 text-[#DAA520] fill-[#DAA520]" />
                                    <span className="text-xs font-medium text-slate-700">
                                      {productReviews[product.id].average.toFixed(1)}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      ({productReviews[product.id].count})
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Price */}
                              <div className="flex items-center gap-2 mt-auto">
                                {hasDiscount && originalPrice !== finalPrice ? (
                                  <>
                                    <span className="text-lg font-bold text-[#008080]">
                                      €{finalPrice.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-slate-400 line-through">
                                      €{originalPrice.toFixed(2)}
                                    </span>
                                    <Badge className={`${crafterDiscount > 0 && launchActive ? 'bg-gradient-to-r from-orange-500 to-green-500' : crafterDiscount > 0 ? 'bg-orange-500' : 'bg-green-500'} text-white text-[10px] px-1.5`}>
                                      -{totalDiscountPercent}%
                                    </Badge>
                                  </>
                                ) : (
                                  <span className="text-lg font-bold text-[#008080]">
                                    €{finalPrice.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="mt-8 text-center">
                      <Button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        variant="outline"
                        size="lg"
                        className="px-8 border-[#008080] text-[#008080] hover:bg-[#008080] hover:text-white"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            Mostra altri prodotti
                            <ChevronDown className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                      <p className="text-sm text-slate-500 mt-2">
                        Visualizzati {products.length} di {totalCount} prodotti
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessun prodotto trovato</h3>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">
                    {searchQuery 
                      ? `Non abbiamo trovato prodotti per "${searchQuery}". Prova con termini diversi.`
                      : 'Non ci sono prodotti che corrispondono ai filtri selezionati.'}
                  </p>
                  <Button onClick={clearAllFilters} className="bg-[#008080] hover:bg-[#006666]">
                    Rimuovi filtri
                  </Button>
                </div>
              )}
            </main>
          </div>
        </div>

        {/* Mobile Filters Drawer */}
        <AnimatePresence>
          {mobileFiltersOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 z-50 md:hidden"
                onClick={() => setMobileFiltersOpen(false)}
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.3 }}
                className="fixed right-0 top-0 bottom-0 w-80 max-w-full bg-white z-50 md:hidden shadow-xl"
              >
                <div className="flex items-center justify-between p-4 border-b border-slate-200">
                  <h2 className="font-semibold text-lg">Filtri</h2>
                  <button
                    onClick={() => setMobileFiltersOpen(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4 overflow-y-auto h-[calc(100%-60px)]">
                  <FilterSidebar onClose={() => setMobileFiltersOpen(false)} />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
